import { AlertCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { useImpersonation } from '@/hooks/useImpersonation';
import { Alert, AlertDescription } from './ui/alert';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export function ImpersonationBanner() {
  const { activeSession, isImpersonating, endImpersonation, getRemainingTime } = useImpersonation();
  const [remainingMinutes, setRemainingMinutes] = useState(0);
  const [impersonatedUserName, setImpersonatedUserName] = useState<string | null>(null);
  const [impersonatedUserRole, setImpersonatedUserRole] = useState<string | null>(null);
  const [impersonatedUserDepartment, setImpersonatedUserDepartment] = useState<string | null>(null);

  useEffect(() => {
    const fetchImpersonatedUserData = async () => {
      if (activeSession?.impersonated_user_id) {
        // Fetch user display info via SECURITY DEFINER RPC
        const { data: info, error: infoError } = await supabase
          .rpc('current_user_info', { _user_id: activeSession.impersonated_user_id });
        if (!infoError && info && Array.isArray(info) && info[0]) {
          const row = info[0] as { full_name: string | null; email: string | null };
          setImpersonatedUserName(row.full_name || row.email || null);
        }

        // Fetch role and department via SECURITY DEFINER RPCs
        const [{ data: role }, { data: dept }] = await Promise.all([
          supabase.rpc('get_user_role', { _user_id: activeSession.impersonated_user_id }),
          supabase.rpc('get_user_department', { _user_id: activeSession.impersonated_user_id })
        ]);
        setImpersonatedUserRole((role as string) || null);
        setImpersonatedUserDepartment((dept as string) || null);
      } else {
        setImpersonatedUserName(null);
        setImpersonatedUserRole(null);
        setImpersonatedUserDepartment(null);
      }
    };

    fetchImpersonatedUserData();
  }, [activeSession?.impersonated_user_id]);

  useEffect(() => {
    if (!isImpersonating) return;

    const updateTime = () => {
      const remaining = getRemainingTime();
      setRemainingMinutes(remaining);
      
      // Auto-refresh page when session expires
      if (remaining <= 0) {
        window.location.reload();
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [isImpersonating, getRemainingTime]);

  const handleEndImpersonation = async () => {
    await endImpersonation();
    window.location.reload(); // Refresh to show real role
  };

  if (!isImpersonating || !activeSession) return null;

  const getRoleColor = (role: string): string => {
    const roleColors: Record<string, string> = {
      'admin': 'bg-purple-500',
      'procurement_manager': 'bg-teal-500',
      'provider_manager': 'bg-orange-500',
      'qa_manager': 'bg-green-500',
      'ma_manager': 'bg-cyan-500',
      'tech_apps': 'bg-emerald-500',
      'tech_infrastructure': 'bg-slate-500',
      'department_manager': 'bg-blue-500'
    };
    return roleColors[role] || 'bg-gray-500';
  };

  const getDisplayRole = () => {
    // If impersonating a user, use their actual role
    if (activeSession.impersonated_user_id && impersonatedUserRole) {
      return impersonatedUserRole;
    }
    // Otherwise use the impersonated role from session
    return activeSession.impersonated_role || '';
  };

  const getImpersonationLabel = () => {
    // If impersonating a specific user, show their name
    if (activeSession.impersonated_user_id && impersonatedUserName) {
      return impersonatedUserName;
    }
    
    // Otherwise show the role
    if (activeSession.impersonated_role) {
      const roleLabels: Record<string, string> = {
        'admin': 'Admin',
        'procurement_manager': 'Procurement Manager',
        'provider_manager': 'Provider Manager',
        'qa_manager': 'QA Manager',
        'ma_manager': 'M&A Manager',
        'tech_apps': 'Tech Apps Manager',
        'tech_infrastructure': 'Tech Infrastructure Manager'
      };
      
      // For department managers, show the specific department manager role
      if (activeSession.impersonated_role === 'department_manager' && activeSession.impersonated_department) {
        const deptLabels: Record<string, string> = {
          'aso': 'ASO Manager',
          'product': 'Product Manager',
          'compliance': 'Compliance Manager',
          'ad_monetization': 'Ad Monetization Manager',
          'user_acquisition': 'User Acquisition Manager'
        };
        return deptLabels[activeSession.impersonated_department] || `${activeSession.impersonated_department} Manager`;
      }
      
      return roleLabels[activeSession.impersonated_role] || activeSession.impersonated_role;
    }
    return 'Unknown';
  };

  const getRoleDisplayLabel = () => {
    const role = getDisplayRole();
    if (!role) return 'User';

    const roleLabels: Record<string, string> = {
      'admin': 'Admin',
      'procurement_manager': 'Procurement Manager',
      'provider_manager': 'Provider Manager',
      'qa_manager': 'QA Manager',
      'ma_manager': 'M&A Manager',
      'tech_apps': 'Tech Apps Manager',
      'tech_infrastructure': 'Tech Infrastructure Manager',
      'department_manager': 'Department Manager'
    };

    if (role === 'department_manager') {
      const dept = activeSession.impersonated_user_id ? impersonatedUserDepartment : activeSession.impersonated_department;
      if (dept) {
        const deptLabels: Record<string, string> = {
          'aso': 'ASO Manager',
          'product': 'Product Manager',
          'compliance': 'Compliance Manager',
          'ad_monetization': 'Ad Monetization Manager',
          'user_acquisition': 'User Acquisition Manager'
        };
        return deptLabels[dept] || `${dept} Manager`;
      }
    }

    return roleLabels[role] || role;
  };

  return (
    <Alert className="fixed bottom-0 left-0 right-0 z-50 rounded-none border-orange-500 bg-orange-50 border-t-2">
      <AlertCircle className="h-5 w-5 text-orange-600" />
      <AlertDescription className="flex items-center justify-between w-full ml-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-orange-800">Viewing as:</span>
            <div className="flex items-center gap-2 bg-white/50 px-3 py-1.5 rounded-md">
              <div className={`w-2.5 h-2.5 rounded-full ${getRoleColor(getDisplayRole())}`}></div>
              <span className="font-bold text-orange-950 text-base">
                {impersonatedUserName ?? getRoleDisplayLabel()}
              </span>
              {impersonatedUserName && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-orange-100 text-orange-900">
                  {getRoleDisplayLabel()}
                </span>
              )}
            </div>
          </div>
          <span className="text-sm text-orange-700 bg-orange-100 px-2 py-1 rounded">
            {remainingMinutes > 0 ? `⏱ ${remainingMinutes} min remaining` : '⚠️ Expiring...'}
          </span>
          {activeSession.reason && (
            <span className="text-sm text-orange-600 italic">
              Reason: {activeSession.reason}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleEndImpersonation}
          className="text-orange-900 hover:text-orange-950 hover:bg-orange-100"
        >
          <X className="h-4 w-4 mr-1" />
          End Impersonation
        </Button>
      </AlertDescription>
    </Alert>
  );
}
